<script setup lang="ts">
  import { computed, inject, onBeforeUnmount, onMounted, reactive, ref, watch, watchEffect } from 'vue'
  import { pendingRefocusKey } from '@/shared/models/pendingRefocus'
  import {
    Form,
    Input,
    InputNumber,
    Select,
    SelectOption,
    Checkbox,
    AutoComplete,
    Button
  } from 'ant-design-vue'
  import ColumnMappingRuleComponent from './ColumnMappingRuleConfiguration.vue'
  import { DataCleaningService } from '../services/DataCleaningService'
  import { MinusSquare, PlusSquare } from 'lucide-vue-next'
  import ArrayRuleBuilder from './ArrayRuleBuilder.vue'
  import { debounceTime, Subject } from 'rxjs'
  import { parseSmartValue, DEFAULT_INPUT_DEBOUNCE_MS } from '@/utils'
  import JsonRuleConfiguration from './JsonRuleConfiguration.vue'
  import { DataCleaningJsonRuleConfigurationService } from '../services/DataCleaningJsonRuleConfigurationService'
  import SelectOrCreateRuleConfiguration from './SelectOrCreateRuleConfiguration.vue'
  import { RuleSchema } from '../models/RuleSchema'
  import { RuleField } from '../models/RuleField'
  import { RuleFieldType } from '../models/RuleFieldType'
  import ColumnMultiSelectOrCreateRuleConfiguration from './ColumnMultiSelectOrCreateRuleConfiguration.vue'
  import { CompressOutlined, ExpandOutlined } from '@ant-design/icons-vue'
  import RuleHelpPopover from '../help/components/RuleHelpPopover.vue'
  import RegexRuleConfiguration from './RegexRuleConfiguration.vue'
  import ColorRuleConfiguration from './ColorRuleConfiguration.vue'
  import StringTemplateBuilder from '@/shared/StringTemplateBuilder/components/StringTemplateBuilder.vue'
  import { AvailableColumn } from '../models/AvailableColumn'
  import { useWindowSize } from '@/composables/useWindowSize'
  import { DataCleaningUiService } from '../services/DataCleaningUiService'
  import { Rule } from '../models/Rule'

  interface IObjectRuleBuilderProps {
    rulesSchemas: Array<RuleSchema>
    ruleField: RuleField
    modelValue: {}
    availableColumns: Array<AvailableColumn>
    configurationLevel: number
    isCondensedLayout: boolean
    globalCollapsed?: { collapsed: boolean }
    rootRule?: Rule | null
    currentPath?: string[]
  }

  interface IObjectRuleBuilderEmits {
    (e: 'update:modelValue', value: {}): void
  }

  const props = defineProps<IObjectRuleBuilderProps>()
  const emit = defineEmits<IObjectRuleBuilderEmits>()

  const modelValue = reactive<{}>(props.modelValue)
  const configuration = ref<Record<string, any>>({})
  const isCollapsed = ref<boolean>(false)
  const globalCollapsed = ref<{ collapsed: boolean }>(props.globalCollapsed ?? { collapsed: false })
  const { controlSize } = useWindowSize()

  // mirror checkbox sizing class used elsewhere
  const checkboxSizeClass = computed(() => {
    if (controlSize.value === 'small') return 'checkbox-sm'
    if (controlSize.value === 'large') return 'checkbox-lg'
    return 'checkbox-md'
  })

  // Initialize configuration with default values
  const initializeConfiguration = () => {
    if (props.ruleField.field_type !== RuleFieldType.Object) return
    // Support nested object structures
    if (
      (props.ruleField.item_schema == null && props.ruleField.fields == null) ||
      (props.ruleField.item_schema != null &&
        props.ruleField.item_schema.field_type !== RuleFieldType.Object)
    )
      throw new Error(
        'ObjectRuleBuilder requires an Object type with fields or child items_schema of type'
      )

    if (props.ruleField.fields != null) {
      configuration.value = DataCleaningService.initializeRuleFieldConfiguration(
        props.ruleField.fields!
      )
    } else if (props.ruleField.item_schema != null) {
      configuration.value = DataCleaningService.initializeRuleConfigurationForField(
        props.ruleField.item_schema
      )
    }
  }

  const handleConfigurationChange = () => {
    if (props.ruleField) {
      emit('update:modelValue', configuration.value)
    }
  }

  // @ts-expect-error
  const handleRuleTypeChanged = (ruleType: string, value: {}) => {
    //TODO: Support object nested custom rules and type conversion
    //if (props.rule) {
    //emit('configuration-updated', { ...props.rule, type: ruleType }, value, true)
    //}
  }

  const pendingRefocus = inject(pendingRefocusKey)

  let isUnmounting = false

  const onInputChange = (newValue: string, fieldName: string) => {
    const activeEl = document.activeElement as HTMLInputElement | null
    if (pendingRefocus) {
      pendingRefocus.value = {
        levelFieldKey: `${props.configurationLevel}:${fieldName}`,
        cursor: activeEl?.selectionStart ?? null
      }
    }
    inputValueChanged$.next({ value: newValue, field: fieldName })
  }

  const onInputBlur = (fieldKey: string) => {
    if (isUnmounting) return
    if (pendingRefocus?.value?.levelFieldKey === `${props.configurationLevel}:${fieldKey}`) {
      pendingRefocus.value = null
    }
  }

  const inputValueChanged$ = new Subject<{ field: string; value: string }>()

  const inputValueChangedSubscription = inputValueChanged$
    .pipe(debounceTime(DEFAULT_INPUT_DEBOUNCE_MS))
    .subscribe(({ field, value }) => {
      const parsedValue = parseSmartValue(value)
      configuration.value[field] = parsedValue
      handleConfigurationChange()
    })

  // Watch for rule changes
  watch(
    modelValue,
    (newVal) => {
      configuration.value = { ...newVal }
    },
    { immediate: true }
  )

  watch(
    () => props.ruleField,
    (newRuleField) => {
      if (newRuleField) {
        if (Object.keys(configuration.value).length === 0) {
          initializeConfiguration()
          handleConfigurationChange()
        }
      }
    },
    { immediate: true }
  )

  watchEffect(() => {
    const newVal = props.globalCollapsed
    if (newVal) {
      globalCollapsed.value = newVal
      isCollapsed.value = !!newVal.collapsed
    }
  })

  const toggleCollapse = () => {
    isCollapsed.value = !isCollapsed.value
  }

  const toggleGlobalCollapse = () => {
    globalCollapsed.value = { collapsed: !isCollapsed.value }
    isCollapsed.value = !isCollapsed.value
  }

  // If only single field, do not use grid css
  const simpleFieldCount = computed(() =>
    props?.ruleField?.fields
      ? props.ruleField.fields.filter(
          (f: any) =>
            f.field_type !== RuleFieldType.Object &&
            f.field_type !== RuleFieldType.Array &&
            f.field_type !== RuleFieldType.Json
        ).length
      : 0
  )

  const shouldShowGlobalCollapseButton = computed(() => {
    return (
      props.ruleField?.fields?.findIndex(
        (x: RuleField) =>
          x.field_type === RuleFieldType.Array ||
          x.field_type === RuleFieldType.Object ||
          x.field_type === RuleFieldType.Json
      ) !== -1
    )
  })

  const shouldShowFieldInformation = (field: RuleField): boolean => {
    return field.field_type !== RuleFieldType.Object && field.field_type !== RuleFieldType.Json
  }

  const displayField = computed(() => {
    const mappedField = props.ruleField?.display_field as string | string[] | null | undefined
    if (mappedField == null) return undefined

    const fields = props.ruleField?.fields ?? []

    const valueForName = (name: string) => {
      const matchingField = fields.find((x) => x.name === name)
      if (!matchingField) return undefined
      const val = configuration.value?.[matchingField.name]
      return val === undefined ? undefined : val
    }

    if (typeof mappedField === 'string') {
      return valueForName(mappedField)
    }

    if (Array.isArray(mappedField)) {
      for (const name of mappedField) {
        const v = valueForName(name)
        if (v) return v
      }
    }

    return undefined
  })

  onMounted(() => {
    if (pendingRefocus?.value) {
      const { levelFieldKey, cursor } = pendingRefocus.value
      let el = document.querySelector(`[data-field="${levelFieldKey}"]`) as HTMLElement | null
      if (el && el.tagName !== 'INPUT') el = el.querySelector('input') ?? el
      const inputEl = el as HTMLInputElement | null
      if (inputEl?.tagName === 'INPUT') {
        inputEl.focus()
        if (cursor !== null) {
          try { inputEl.setSelectionRange(cursor, cursor) } catch {}
        }
      }
      pendingRefocus.value = null
    }
  })

  onBeforeUnmount(() => {
    isUnmounting = true
    inputValueChangedSubscription?.unsubscribe()
  })

  const isObjectWithNestedObject = computed(
    () => props.ruleField.fields == null && props.ruleField.item_schema != null
  )
</script>

<template>
  <div v-if="isObjectWithNestedObject" class="object-rule-builder-grid-span">
    <ObjectRuleBuilder
      :configuration-level="props.configurationLevel"
      :available-columns="props.availableColumns"
      v-model="configuration[props.ruleField.item_schema!.name]"
      :rule-field="props.ruleField.item_schema!"
      @update:modelValue="handleConfigurationChange"
      :rules-schemas="rulesSchemas"
      :is-condensed-layout="props.isCondensedLayout"
      :global-collapsed="globalCollapsed"
      :root-rule="props.rootRule"
    />
  </div>
  <div v-else>
    <div
      v-if="ruleField"
      :class="[
        'border',
        'rule-configuration',
        'h-full',
        'w-full',
        {
          'child-rule-configuration': props.configurationLevel > 1,
          '!bg-[var(--card)] dark:!bg-[var(--card-light)]': props.configurationLevel % 2 !== 0,
          '!bg-[var(--card-light)] dark:!bg-[var(--card)]': props.configurationLevel % 2 === 0
        }
      ]"
    >
      <div class="flex h-full flex-col">
        <!-- Rule Header -->
        <div class="horizontal-rule mb-4 border-b pb-1">
          <div class="flex">
            <div>
              <h3 class="mb-1 text-lg font-semibold">
                Configure: {{ ruleField.display_name }}
                <span v-if="displayField != null">{{ `(${displayField})` }}</span>
              </h3>
            </div>

            <div class="ml-auto flex" v-if="props.configurationLevel > 1">
              <a-tooltip placement="top">
                <template #title>Toggle Collapse</template>
                <Button
                  style="padding: 3px; border: none"
                  type="text"
                  :size="controlSize"
                  :outlined="false"
                  class="flex items-center justify-center"
                  @click="toggleCollapse"
                  aria-label="Toggle Collapse"
                >
                  <PlusSquare
                    v-if="isCollapsed"
                    class="text-base"
                    style="line-height: 1; margin: 0"
                  />
                  <MinusSquare v-else class="text-base" style="line-height: 1; margin: 0" />
                </Button>
              </a-tooltip>

              <a-tooltip v-if="shouldShowGlobalCollapseButton" placement="top">
                <template #title>{{ isCollapsed ? 'Expand All' : 'Collapse All' }}</template>
                <Button
                  style="padding: 3px; border: none"
                  type="text"
                  :size="controlSize"
                  class="flex items-center justify-center"
                  @click="toggleGlobalCollapse"
                  aria-label="Toggle Collapse All"
                >
                  <ExpandOutlined
                    v-if="isCollapsed"
                    class="rotate-90 rounded-sm border-gray-300 p-1 text-base"
                    style="line-height: 1; margin: 0"
                  />
                  <CompressOutlined
                    v-else
                    class="rotate-90 rounded-sm border-gray-300 p-1 text-base"
                    style="line-height: 1; margin: 0"
                  />
                </Button>
              </a-tooltip>
            </div>
          </div>
        </div>

        <!-- Configuration Form -->
        <div
          :class="['flex-1', 'overflow-y-auto', { 'collapsed-rule-configuration': isCollapsed }]"
        >
          <Form ref="formRef" :model="configuration" layout="vertical">
            <div :class="{ 'object-rule-fields-grid': true, expanded: !props.isCondensedLayout }">
              <div
                v-for="field in ruleField.fields"
                :key="field.name"
                class="object-rule-form-field-grid"
                :class="{
                  'array-rule-builder-grid-span': field.field_type === RuleFieldType.Array,
                  'object-rule-builder-grid-span': field.field_type === RuleFieldType.Object,
                  'json-rule-configuration-grid-span': field.field_type === RuleFieldType.Json,
                  'single-field-grid-span':
                    simpleFieldCount <= 1 &&
                    field.field_type !== RuleFieldType.Object &&
                    field.field_type !== RuleFieldType.Array &&
                    field.field_type !== RuleFieldType.Json,
                  '!hidden': !DataCleaningUiService.isFieldVisibleFromRoot(
                    field,
                    props.rootRule,
                    props.currentPath ? [...props.currentPath, field.name] : [field.name]
                  )
                }"
              >
                <div class="flex">
                  <div v-if="shouldShowFieldInformation(field)" class="min-w-0 flex-1">
                    <label class="table-field-label mb-1 block text-sm font-medium">
                      {{ field.display_name }}
                      <span v-if="field.required" class="text-red-500">*</span>
                    </label>
                    <p v-if="field.description" class="table-field-label mb-2 text-xs">
                      {{ field.description }}
                    </p>
                  </div>

                  <div
                    v-if="field.help_content != null && field.help_content?.length > 0"
                    class="m1-auto flex justify-end"
                  >
                    <RuleHelpPopover
                      :content-type="field.help_content"
                      :title="`${field.display_name} Help`"
                    />
                  </div>
                </div>

                <!-- Text Input -->
                <Input
                  v-if="field.field_type === RuleFieldType.Text"
                  :value="configuration[field.name]"
                  @update:value="(val: string) => onInputChange(val, field.name)"
                  @blur="() => onInputBlur(field.name)"
                  :data-field="`${props.configurationLevel}:${field.name}`"
                  :placeholder="field.placeholder || ''"
                  :readonly="field.readonly"
                  :disabled="field.readonly"
                  :size="controlSize"
                />

                <!-- Number Input -->
                <InputNumber
                  v-else-if="field.field_type === RuleFieldType.Number"
                  v-model:value="configuration[field.name]"
                  :placeholder="field.placeholder || ''"
                  :size="controlSize"
                  @change="handleConfigurationChange"
                  :readonly="field.readonly"
                  :disabled="field.readonly"
                  class="w-full"
                />

                <RegexRuleConfiguration
                  v-else-if="field.field_type === RuleFieldType.RegularExpression"
                  v-model="configuration[field.name]"
                  :required="field.required"
                  :regex="field.field_type_args?.regex_pattern"
                  :data-type="field.field_type_args?.regex_data_type"
                  :placeholder="field.placeholder || ''"
                  :error-message="field.field_type_args?.regex_description"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Select Dropdown -->
                <Select
                  v-else-if="field.field_type === RuleFieldType.Select"
                  v-model:value="configuration[field.name]"
                  :placeholder="`Select ${field.display_name.toLowerCase()}`"
                  @change="handleConfigurationChange"
                  :size="controlSize"
                  :readonly="field.readonly"
                  :disabled="field.readonly"
                  class="w-full"
                  showSearch
                  allowClear
                >
                  <SelectOption
                    v-for="option in field.options"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </SelectOption>
                </Select>

                <!-- Select Dropdown JSON Payload -->
                <Select
                  v-else-if="field.field_type === RuleFieldType.SelectJson"
                  :value="JSON.stringify(configuration[field.name])"
                  :placeholder="`Select ${field.display_name.toLowerCase()}`"
                  @update:value="
                    (val: string) => {
                      try {
                        const parsed = JSON.parse(val)
                        configuration[field.name] = parsed
                        handleConfigurationChange()
                      } catch (e) {
                        console.error('Failed to parse selected JSON value', e)
                      }
                    }
                  "
                  :size="controlSize"
                  :readonly="field.readonly"
                  :disabled="field.readonly"
                  class="w-full"
                  showSearch
                  allowClear
                >
                  <SelectOption
                    v-for="option in field.options"
                    :key="JSON.stringify(option.value)"
                    :value="JSON.stringify(option.value)"
                  >
                    <span v-html="option.label"></span>
                  </SelectOption>
                </Select>

                <!-- Select Dropdown Or Create -->
                <SelectOrCreateRuleConfiguration
                  v-else-if="field.field_type === RuleFieldType.SelectOrCreate"
                  v-model="configuration[field.name]"
                  :placeholder="`Select or enter new ${field.display_name.toLowerCase()}`"
                  :options="field.options || []"
                  :readonly="field.readonly"
                  :disabled="field.readonly"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Multi-Select -->
                <Select
                  v-else-if="
                    field.field_type === RuleFieldType.MultiSelect ||
                    field.field_type === RuleFieldType.ColumnMultiSelect
                  "
                  v-model:value="configuration[field.name]"
                  mode="multiple"
                  :placeholder="`Select ${field.display_name.toLowerCase()}`"
                  @change="handleConfigurationChange"
                  :size="controlSize"
                  :readonly="field.readonly"
                  :disabled="field.readonly"
                  class="w-full"
                  showSearch
                  allowClear
                >
                  <SelectOption
                    v-if="field.field_type === RuleFieldType.MultiSelect"
                    v-for="option in field.options"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </SelectOption>

                  <SelectOption
                    v-if="field.field_type === RuleFieldType.ColumnMultiSelect"
                    v-for="option in props.availableColumns"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </SelectOption>
                </Select>

                <!-- Boolean Checkbox -->
                <Checkbox
                  v-else-if="field.field_type === RuleFieldType.Boolean"
                  @change="handleConfigurationChange"
                  v-model:checked="configuration[field.name]"
                  :readonly="field.readonly"
                  :disabled="field.readonly"
                  :class="checkboxSizeClass"
                >
                  {{ field.display_name }}
                </Checkbox>

                <!-- Column Select -->
                <Select
                  v-else-if="field.field_type === RuleFieldType.ColumnSelect"
                  v-model:value="configuration[field.name]"
                  :placeholder="`Select ${field.display_name.toLowerCase()}`"
                  @change="handleConfigurationChange"
                  :size="controlSize"
                  :readonly="field.readonly"
                  :disabled="field.readonly"
                  F
                  class="w-full"
                  showSearch
                  allowClear
                >
                  <SelectOption
                    v-for="column in props.availableColumns"
                    :key="column.value"
                    :value="column.value"
                  >
                    {{ column.label }}
                  </SelectOption>
                </Select>

                <!-- Column Select or Create -->
                <AutoComplete
                  v-else-if="field.field_type === RuleFieldType.ColumnSelectOrCreate"
                  v-model:value="configuration[field.name]"
                  :options="props.availableColumns"
                  :placeholder="
                    field.placeholder || `Select existing column or enter new column name`
                  "
                  :size="controlSize"
                  class="w-full"
                  :readonly="field.readonly"
                  :disabled="field.readonly"
                  @change="handleConfigurationChange"
                  showSearch
                  allowClear
                />

                <!-- Multi Column Select Or Create -->
                <ColumnMultiSelectOrCreateRuleConfiguration
                  v-else-if="field.field_type === RuleFieldType.ColumnMultiSelectOrCreate"
                  v-model="configuration[field.name]"
                  :placeholder="`Select or enter new ${field.display_name.toLowerCase()} values`"
                  :available-columns="props.availableColumns"
                  @update:modelValue="handleConfigurationChange"
                />

                <ColumnMappingRuleComponent
                  v-else-if="field.field_type === RuleFieldType.SourceDestinationPairArray"
                  :available-columns="props.availableColumns"
                  v-model="configuration[field.name]"
                  :place-holder="field.placeholder"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Color Pickter -->
                <ColorRuleConfiguration
                  v-else-if="field.field_type === RuleFieldType.ColorPicker"
                  v-model="configuration[field.name]"
                  :place-holder="field.placeholder || 'Select color'"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Array Type -->
                <ArrayRuleBuilder
                  v-else-if="field.field_type === RuleFieldType.Array"
                  :configuration-level="props.configurationLevel"
                  :available-columns="props.availableColumns"
                  v-model="configuration[field.name]"
                  :rule-field="field"
                  @update:modelValue="handleConfigurationChange"
                  :rules-schemas="rulesSchemas"
                  :is-condensed-layout="props.isCondensedLayout"
                  :global-collapsed="globalCollapsed"
                />

                <!-- Object Type (nested) -->
                <ObjectRuleBuilder
                  v-else-if="field.field_type === RuleFieldType.Object"
                  :configuration-level="props.configurationLevel + 1"
                  :available-columns="props.availableColumns"
                  v-model="configuration[field.name]"
                  :rule-field="field"
                  @update:modelValue="handleConfigurationChange"
                  :rules-schemas="rulesSchemas"
                  :is-condensed-layout="props.isCondensedLayout"
                  :global-collapsed="globalCollapsed"
                  :root-rule="props.rootRule"
                  :current-path="
                    props.currentPath ? [...props.currentPath, field.name] : [field.name]
                  "
                />

                <!-- String Template -->
                <StringTemplateBuilder
                  v-else-if="field.field_type === RuleFieldType.StringTemplate"
                  :configuration-level="props.configurationLevel + 1"
                  :available-columns="props.availableColumns"
                  :delimiter_start="field.field_type_args?.delimiter_start"
                  :delimiter_end="field.field_type_args?.delimiter_end"
                  :place-holder="field.placeholder"
                  v-model="configuration[field.name]"
                  @update:modelValue="handleConfigurationChange"
                  :is-condensed-layout="props.isCondensedLayout"
                  :global-collapsed="globalCollapsed"
                />

                <!-- Json Type (Custom Rule) -->
                <JsonRuleConfiguration
                  v-else-if="field.field_type === RuleFieldType.Json"
                  :rule-schemas="props.rulesSchemas"
                  :available-columns="props.availableColumns"
                  v-model="configuration"
                  :editor-schema="
                    DataCleaningJsonRuleConfigurationService.getJsonEditorSchemaForRule()
                  "
                  :place-holder="field.placeholder || 'Configure Json'"
                  :configuration-level="props.configurationLevel"
                  @rule-type-changed="handleRuleTypeChanged"
                  @update:modelValue="handleConfigurationChange"
                  :allow-type-change="true"
                  :global-collapsed="globalCollapsed"
                />

                <!-- Default fallback for unknown field types -->
                <JsonRuleConfiguration
                  v-else
                  :rule-schemas="props.rulesSchemas"
                  :available-columns="props.availableColumns"
                  v-model="configuration[field.name]"
                  :editor-schema="
                    DataCleaningJsonRuleConfigurationService.getJsonEditorSchemaForField(field)
                  "
                  :place-holder="field.placeholder || 'Configure Json'"
                  :configuration-level="props.configurationLevel"
                  @update:modelValue="handleConfigurationChange"
                  :allow-type-change="false"
                  :global-collapsed="globalCollapsed"
                />
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .object-rule-fields-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
  }

  @media (min-width: 1100px) {
    .object-rule-fields-grid:not(.expanded) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .object-rule-form-field-grid {
    width: 100%;
    min-height: 90px;
    max-height: 110px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    overflow: hidden;
  }

  .array-rule-builder-grid-span,
  .object-rule-builder-grid-span,
  .json-rule-configuration-grid-span {
    grid-column: 1 / -1 !important;
    min-height: unset;
    max-height: unset;
    overflow: visible;
  }

  .collapsed-rule-configuration {
    display: none;
  }

  .rule-configuration {
    padding: 10px;
    border-radius: 4px;
  }

  .horizontal-rule {
    border-bottom-color: #0000001f;
  }

  /* Custom scrollbar for form area */
  .overflow-y-auto::-webkit-scrollbar {
    width: 6px;
  }

  .overflow-y-auto::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
</style>
